import {
  type OvertimeSummary,
  type StartWorkSessionInput,
  type TimesheetEntryDetail,
  type TimesheetState,
  type TimesheetWeek,
} from '@apm/contracts';
import { apiFetch } from '../../lib/http';

/**
 * Bấm giờ làm việc — APM-TIME.
 *
 * Bốn endpoint đầu chỉ đụng tới **chính người đang đăng nhập**; `week` là cái
 * duy nhất đọc được của người khác, và nó đi qua chuỗi quản lý ở server.
 */
export const timesheetApi = {
  /** Trạng thái cho cái nút ở góc màn hình: đang chạy gì, ca hôm nay tới mấy giờ. */
  state: () => apiFetch<TimesheetState>('/timesheet/state'),

  /** Bắt đầu — đang bấm việc khác thì server tự đóng đoạn cũ với `switched`. */
  start: (input: StartWorkSessionInput) =>
    apiFetch<TimesheetState>('/timesheet/start', { method: 'POST', body: input }),

  pause: () => apiFetch<TimesheetState>('/timesheet/pause', { method: 'POST', body: {} }),
  stop: () => apiFetch<TimesheetState>('/timesheet/stop', { method: 'POST', body: {} }),

  /**
   * Tổng giờ làm thêm 90 ngày của **chính mình** — APM-OT-d.
   *
   * Chỉ của mình: quản lý xem của cấp dưới thì đi qua bảng tuần, nơi phạm vi và
   * audit đã được giải quyết một lần.
   */
  overtime: () => apiFetch<OvertimeSummary>('/timesheet/overtime'),

  /** `scope: 'team'` cần quyền; mỗi lượt ghi một dòng `sensitive_access_log`. */
  week: (week: string, scope: 'me' | 'team') =>
    apiFetch<TimesheetWeek>(
      `/timesheet/week?week=${encodeURIComponent(week)}${scope === 'team' ? '&scope=team' : ''}`,
    ),

  /**
   * Từng phiên bấm giờ của một người trên một task trong một ngày, kèm % tăng
   * thêm, checklist và cờ hoàn thành — APM-TSOUT. Người khác ⇒ ghi audit.
   */
  entry: (userId: string, taskId: string, date: string) =>
    apiFetch<TimesheetEntryDetail>(
      `/timesheet/entry?userId=${encodeURIComponent(userId)}&taskId=${encodeURIComponent(taskId)}&date=${encodeURIComponent(date)}`,
    ),
};

export const timesheetKeys = {
  state: () => ['timesheet', 'state'] as const,
  overtime: () => ['timesheet', 'overtime'] as const,
  week: (week: string, scope: 'me' | 'team') => ['timesheet', 'week', week, scope] as const,
  entry: (userId: string, taskId: string, date: string) =>
    ['timesheet', 'entry', userId, taskId, date] as const,
};
